import type { API } from "@/api/client"
import { deleteSession, getActiveSessions, getSessionChunks } from "@/lib/chunkDb"
import { silentError } from "@/shared/lib/silentError"
import { createUploadQueue } from "./chunkUploadQueue"

const STALE_SESSION_MS = 24 * 60 * 60 * 1000

/** Resume or purge IndexedDB sessions left after a reload (exception 2.7). */
export function recoverOrphanSessions(
    api: API<unknown>,
    opts: { isCancelled: () => boolean; isActiveSession: (sessionId: string) => boolean },
): void {
    getActiveSessions()
        .then((sessions) => {
            for (const session of sessions) {
                getSessionChunks(session.sessionId)
                    .then((chunks) => {
                        if (opts.isCancelled() || opts.isActiveSession(session.sessionId)) return
                        if (chunks.length === 0) {
                            deleteSession(session.sessionId).then(undefined, (err) =>
                                silentError("indexedDb", err),
                            )
                            return
                        }
                        const sorted = [...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex)
                        const total = session.totalChunks
                        const present = new Set(sorted.map((c) => c.chunkIndex))
                        const complete =
                            total != null &&
                            total > 0 &&
                            Array.from({ length: total }, (_, i) => i).every((i) => present.has(i))
                        if (!complete) {
                            const ageMs = Date.now() - new Date(session.creationDate).getTime()
                            if (ageMs > STALE_SESSION_MS) {
                                deleteSession(session.sessionId).then(undefined, (err) =>
                                    silentError("indexedDb", err),
                                )
                            }
                            return
                        }
                        const pending = sorted.filter((c) => !c.uploaded)
                        let finalized = false
                        const finalize = () => {
                            if (finalized) return
                            finalized = true
                            // Recovery after reload — IndexedDB source of truth (exception 2.7).
                            api.v1
                                .completeUpload(session.sessionId, {
                                    profile_key: session.profileId,
                                    creation_date: session.creationDate,
                                    total_parts: total,
                                    copilot_session_id: session.copilotSessionId,
                                })
                                .then(() => deleteSession(session.sessionId))
                                .then(undefined, (err) => silentError("completeUpload.recovery", err))
                        }
                        if (pending.length === 0) {
                            finalize()
                            return
                        }
                        const uploader = createUploadQueue(
                            api,
                            session.sessionId,
                            () => session.copilotSessionId,
                            {
                                onDrained: finalize,
                                onSessionClosed: () =>
                                    deleteSession(session.sessionId).then(undefined, (err) =>
                                        silentError("indexedDb", err),
                                    ),
                                onQuotaExceeded: () => {
                                    const uploaded = sorted.filter((c) => c.uploaded)
                                    if (uploaded.length === 0) {
                                        deleteSession(session.sessionId).then(undefined, (err) =>
                                            silentError("indexedDb", err),
                                        )
                                        return
                                    }
                                    const parts = Math.max(...uploaded.map((c) => c.chunkIndex)) + 1
                                    api.v1
                                        .completeUpload(session.sessionId, {
                                            profile_key: session.profileId,
                                            creation_date: session.creationDate,
                                            total_parts: parts,
                                            copilot_session_id: session.copilotSessionId,
                                        })
                                        .then(() => deleteSession(session.sessionId))
                                        .then(undefined, (err) =>
                                            silentError("completeUpload.recovery", err),
                                        )
                                },
                            },
                        )
                        for (const chunk of pending) {
                            uploader.enqueue(chunk.chunkIndex, chunk.blob)
                        }
                    })
                    .then(undefined, (err) => silentError("indexedDb", err))
            }
        })
        .then(undefined, (err) => silentError("indexedDb", err))
}
